import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import styled, { createGlobalStyle } from "styled-components"
import { media } from "src/styles/breakpoints"
import Seo from "src/components/SEO"
import BaseContainer from "src/components/Base/BaseContainer"
import ContactForm from "src/components/Form/ContactForm"

// Flip to true to restore the contact form (currently hidden).
const SHOW_CONTACT_FORM = false

const ContactPage = () => {
  const { heroImage } = useStaticQuery(graphql`
    {
      heroImage: file(relativePath: { eq: "contact-hero.jpg" }) {
        publicURL
      }
    }
  `)
  const heroUrl = heroImage?.publicURL

  return (
    <Wrapper className="contact-page">
      <ContactAccordionStyle />
      <Seo
        title="Contact us"
        description="Contact Anima International — media and press, jobs, volunteering, donations, and national organisations."
      />

      {/* ---------- Hero ---------- */}
      <Hero $hasImage={!!heroUrl} style={heroUrl ? { backgroundImage: `url(${heroUrl})` } : undefined}>
        <HeroOverlay />
        <HeroContent>
          <HeroTitle>
            Got a question?
            <br />
            <HeroTitleLight>Get in touch with us.</HeroTitleLight>
          </HeroTitle>
        </HeroContent>
      </Hero>

      {/* ---------- Accordions ---------- */}
      <Main>
        <Container>
          <Accordion>
            {/* Media and press */}
            <details>
              <summary>Media and press</summary>
              <Panel>
                <P>
                  For interviews, comment, footage, images, data, or anything
                  else you need for a story, please contact:
                </P>
                <Contacts>
                  <li>
                    <Where>Global</Where>
                    <ContactInfo>
                      <Who>Anna Bearne</Who>
                      <Role>Global Communications Specialist</Role>
                      <Detail>
                        <a href="mailto:anna.bearne@animainternational.org">
                          anna.bearne@animainternational.org
                        </a>
                      </Detail>
                    </ContactInfo>
                  </li>
                </Contacts>

                <SubLabel>National organizations</SubLabel>
                <Contacts>
                  <li>
                    <Where>Bulgaria</Where>
                    <ContactInfo>
                      <Detail>
                        <a href="mailto:contact@nevidimi.bg">
                          contact@nevidimi.bg
                        </a>
                      </Detail>
                      <Tel>+359 877 970 722</Tel>
                    </ContactInfo>
                  </li>
                  <li>
                    <Where>Denmark</Where>
                    <ContactInfo>
                      <Detail>
                        <a href="https://anima.dk/presse">
                          anima.dk/presse
                        </a>
                      </Detail>
                    </ContactInfo>
                  </li>
                  <li>
                    <Where>France</Where>
                    <ContactInfo>
                      <Who>Keyvan Mostafavi</Who>
                      <Detail>
                        <a href="mailto:keyvan.mostafavi@animafrance.org">
                          keyvan.mostafavi@animafrance.org
                        </a>
                      </Detail>
                    </ContactInfo>
                  </li>
                  <li>
                    <Where>Norway</Where>
                    <ContactInfo>
                      <Who>Niklas Fjeldberg</Who>
                      <Detail>
                        <a href="mailto:niklas.fjeldberg@animanorge.no">
                          niklas.fjeldberg@animanorge.no
                        </a>
                      </Detail>
                    </ContactInfo>
                  </li>
                  <li>
                    <Where>Poland</Where>
                    <ContactInfo>
                      <Who>Anna Iżyńska-Tymoniuk</Who>
                      <Detail>
                        <a href="mailto:media@otwarteklatki.pl">
                          media@otwarteklatki.pl
                        </a>
                      </Detail>
                      <Tel>+48 663 923 178</Tel>
                    </ContactInfo>
                  </li>
                  <li>
                    <Where>UK</Where>
                    <ContactInfo>
                      <Who>Connor Jackson</Who>
                      <Detail>
                        <a href="mailto:connor.jackson@anima.org.uk">
                          connor.jackson@anima.org.uk
                        </a>
                      </Detail>
                      <Tel>+44 7504 580 011</Tel>
                    </ContactInfo>
                  </li>
                </Contacts>
              </Panel>
            </details>

            {/* Jobs */}
            <details>
              <summary>Jobs</summary>
              <Panel>
                <P>
                  Open roles from across all of our organizations are listed on
                  our{" "}
                  <a href="/get-involved/jobs">jobs page</a>.
                </P>
                <P>
                  If you would like to work with us but can't see any suitable
                  open roles, please{" "}
                  <a href="https://anima-international.typeform.com/gen-interest">
                    register your interest here
                  </a>
                  . We'll get in touch when a new opportunity opens up.
                </P>
                <P>
                  We put great effort into making our recruitment process fair
                  and unbiased. For this reason,{" "}
                  <strong>we don't accept CVs via email</strong> and we do not
                  hire candidates outside of the formal recruitment route.
                </P>
                <P>
                  For any other careers-related enquiries, including questions
                  about an ongoing recruitment you're taking part in, email{" "}
                  <a href="mailto:join@animainternational.org">
                    join@animainternational.org
                  </a>
                </P>
              </Panel>
            </details>

            {/* Volunteering */}
            <details>
              <summary>Volunteering</summary>
              <Panel>
                <P>
                  There are many ways you can get involved and help animals with
                  us. Please check our local organizations' pages for details on
                  how you can help:
                </P>
                <Pills>
                  <PillLink href="https://nevidimi.bg/volunteer/">
                    Bulgaria
                  </PillLink>
                  <PillLink href="https://anima.dk/sadan-kan-du-hjaelpe">
                    Denmark
                  </PillLink>
                  <PillLink href="https://animafrance.org/rejoins-le-mouvement">
                    France
                  </PillLink>
                  <PillLink href="https://animanorge.no/jobb">
                    Norway
                  </PillLink>
                  <PillLink href="https://otwarteklatki.pl/dzialaj-z-nami/wolontariat">
                    Poland
                  </PillLink>
                  <PillLink href="https://anima.org.uk/take-action">
                    UK
                  </PillLink>
                </Pills>
                <Note>
                  We currently don't have any volunteering opportunities on our
                  global team.
                </Note>
              </Panel>
            </details>

            {/* Funding */}
            <details>
              <summary>Requests for funding or financial support</summary>
              <Panel>
                <P>
                  We are deeply grateful to everyone who commits their time and
                  money to helping animals. Every life you improve or
                  save is a worthwhile effort. Unfortunately,{" "}
                  <strong>
                    we are not able to help fund veterinary care, feed, land,
                    equipment, or emergency relief
                  </strong>{" "}
                  for animal shelters, sanctuaries, rescue groups, and
                  individuals taking care of animals in need.
                </P>
                <P>
                  Anima International is funded by donations and grants, and we
                  are obligated to spend our funds on our statutory goals –
                  programs helping animals suffering in unfathomable numbers on
                  factory farms.
                </P>
                <SubLabel>Funding for animal advocacy work</SubLabel>
                <P>
                  There are many grantmakers who fund work in this field and
                  accept grant applications. Have a look at:
                </P>
                <Pills>
                  <PillLink href="https://animalcharityevaluators.org/movement-grants/">
                    Animal Charity Evaluators
                  </PillLink>
                  <PillLink href="https://www.navigation.org/">
                    Navigation Fund
                  </PillLink>
                  <PillLink href="https://funds.effectivealtruism.org/funds/animal-welfare">
                    EA Animal Welfare Fund
                  </PillLink>
                </Pills>
              </Panel>
            </details>

            {/* Cruelty */}
            <details>
              <summary>Animal cruelty reporting</summary>
              <Panel>
                <P>
                  If you've witnessed an animal being harmed, please report it
                  to the police, a local animal protection organization, or the
                  national animal welfare authority in the country where it
                  happened.
                </P>
                <P>We are not equipped to intervene in such cases.</P>
              </Panel>
            </details>

            {/* Donations */}
            <details>
              <summary>Amending or cancelling a donation</summary>
              <Panel>
                <P>
                  If you signed up through our{" "}
                  <a href="/donate">Donate page</a>, the confirmation email
                  should include a link for managing your donation. There, you
                  can pause it, change the amount, move the payment date, or
                  cancel.
                </P>
                <P>
                  To contact us about your donation, email{" "}
                  <a href="mailto:info@animainternational.org">
                    info@animainternational.org
                  </a>
                </P>
              </Panel>
            </details>

            {/* General */}
            <details open>
              <summary>Other questions and general enquiries</summary>
              <Panel>
                <P>
                  Email us at{" "}
                  <a href="mailto:info@animainternational.org">
                    info@animainternational.org
                  </a>
                </P>
              </Panel>
            </details>
          </Accordion>
        </Container>
      </Main>

      {SHOW_CONTACT_FORM && (
        <Main>
          <Container>
            <ContactForm />
          </Container>
        </Main>
      )}
    </Wrapper>
  )
}

export default ContactPage

/* Details/summary global styling for the contact page accordion */
const ContactAccordionStyle = createGlobalStyle`
  .contact-page details {
    border-bottom: 1px solid #e4e8e7;
  }

  .contact-page summary {
    list-style: none;
    cursor: pointer;
    padding: 24px 44px 24px 0;
    position: relative;
    font-weight: 600;
    font-size: 21px;
    line-height: 1.3;
    color: #15201f;
    letter-spacing: -0.01em;
  }

  .contact-page summary::-webkit-details-marker {
    display: none;
  }

  .contact-page summary:hover {
    color: #2e8a85;
  }

  .contact-page summary::after {
    content: "";
    position: absolute;
    right: 6px;
    top: 33px;
    width: 11px;
    height: 11px;
    border-right: 2.5px solid #52bbb3;
    border-bottom: 2.5px solid #52bbb3;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
  }

  .contact-page details[open] summary::after {
    transform: rotate(-135deg);
    top: 37px;
  }

  .contact-page .contacts li {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 4px 18px;
    padding: 16px 0;
    border-top: 1px solid #e4e8e7;
    font-size: 16px;
    line-height: 1.5;
  }

  .contact-page .contacts li + li {
    margin-top: 24px;
  }

  .contact-page .contacts li:first-child {
    border-top: 0;
  }

  @media (max-width: 560px) {
    .contact-page .contacts li {
      grid-template-columns: 1fr;
      gap: 2px;
    }

    .contact-page summary {
      font-size: 19px;
      padding-right: 38px;
    }
  }
`

/* ---------------- Styled Components ---------------- */

const Wrapper = styled.div`
  position: relative;
`

/* Hero */
const Hero = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: flex-end;
  background-size: cover;
  background-position: center 40%;
  background-color: #1e5f5c;

  ${media.tablet} {
    max-height: 70vw;
  }

  @media (max-width: 560px) {
    min-height: 60vh;
  }
`

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(6, 58, 55, 0.25) 0%,
    rgba(6, 58, 55, 0.45) 45%,
    rgba(6, 58, 55, 0.82) 100%
  );
  z-index: 1;
`

const HeroContent = styled(BaseContainer)`
  position: relative;
  z-index: 2;
  padding-top: 120px;
  padding-bottom: 33vh;
`

const HeroTitle = styled.h1`
  margin: 0;
  color: #fff;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-size: 30px;
  line-height: 1.22;
  text-shadow: 0 1px 24px rgba(0, 0, 0, 0.28);

  ${media.tablet} {
    font-size: 48px;
  }

  @media (max-width: 560px) {
    font-size: 24px;
  }
`

const HeroTitleLight = styled.span`
  display: block;
  font-weight: 400;
  opacity: 0.85;
`

/* Main */
const Main = styled.main`
  padding: 64px 0 80px;
`

const Container = styled(BaseContainer)`
  max-width: 820px;
`

/* Accordion */
const Accordion = styled.div`
  border-top: 1px solid #e4e8e7;
`

/* Panels */
const Panel = styled.div`
  padding: 0 0 30px;
`

const P = styled.p`
  margin: 0 0 16px;
  font-size: 17px;
  line-height: 1.7;
  color: #3d4746;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: #2e8a85;
    text-decoration: none;
    font-weight: 700;
  }

  a:hover {
    text-decoration: underline;
  }

  strong {
    color: #15201f;
  }
`

/* Contact rows */
const Contacts = styled.ul`
  list-style: none;
  margin: 0 0 4px;
  padding: 0;
`

const Where = styled.span`
  font-weight: 600;
  color: #15201f;
  grid-column: 1;
`

const ContactInfo = styled.span`
  display: contents;
`

const Who = styled.span`
  display: block;
  color: #15201f;
`

const Role = styled.span`
  display: block;
  color: #6e7a79;
  font-size: 14.5px;
`

const Detail = styled.span`
  display: block;
`

const Tel = styled.span`
  display: block;
  color: #6e7a79;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
`

/* Sub-label */
const SubLabel = styled.p`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #6e7a79;
  margin: 26px 0 10px;
`

/* Pills */
const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin: 0 0 16px;
`

const PillLink = styled.a`
  font-size: 14.5px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 4px;
  background: #eaf7f6;
  border: 1px solid #cdeae7;
  color: #1e5f5c;
  text-decoration: none;

  &:hover {
    background: #cdeae7;
    text-decoration: none;
  }
`

const Note = styled.p`
  font-size: 15px;
  color: #6e7a79;
`